import {
  Center,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { CenteredSpinner } from '@duke-hq/app-provider-web/components/Loading';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { Plural, Trans } from '@lingui/react/macro';
import { ArrowRightFromLine } from 'lucide-react';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { EnrolmentDetails } from '../EnrolmentDetails';
import { testIds } from './Enrolments.testIds';
import { EnrolmentsTable } from './EnrolmentsTable';
import { CourseReferralButton } from '../components/CourseReferralModal';
import QuickReferralModal from '../components/QuickReferralModal';
import { useTenantContext } from '@duke-hq/app-provider-web/tenant/TenantContext';
import ProviderFilterBar from '../../components/FilterBar/ProviderFilterBar';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { emptyPaginationResponse, SortOrder, parseSortOrder } from '@duke-hq/lib/pagination';

export function Enrolments(): React.JSX.Element {
  const tenantCtx = useTenantContext();
  const isTenantUsingNewReferralProcess = tenantCtx?.settings.uses_new_referral_process ?? false;
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const [querySearch, setQuerySearch] = useState('');
  const { pageSize, currentPage, setPage } = usePagination();
  const navigate = useNavigate();
  const { enrolmentId } = useParams<{ enrolmentId: string }>();
  const [searchParams] = useSearchParams();
  const { filters, filtersLoaded } = useFiltersContext();

  const allFilters = useMemo(() => {
    return {
      sort: parseSortOrder(searchParams.get('sort'), SortOrder.DESC),
      orderBy: searchParams.get('orderBy') ?? 'created_at',
      status: searchParams.get('status') ?? undefined,
      health: filters.health?.join(','),
      recentPaceStatus: filters.recentPaceStatus?.join(','),
      siteLocationIds: filters.sites,
      siteRegionIds: filters.regions,
      consultantIds: filters.consultants?.join(','),
      courseIds: filters.courses?.join(','),
      dueDateStart: filters.dueDateStart,
      dueDateEnd: filters.dueDateEnd,
      lastActivityStart: searchParams.get('lastActivityStart') ?? undefined,
      lastActivityEnd: searchParams.get('lastActivityEnd') ?? undefined,
      percent: filters.percent,
      contractIds: filters.contractIds?.join(','),
    };
  }, [filters, searchParams]);

  const { data: paginatedEnrolments, isFetching } = trpcTenant.enrolments.all.useQuery(
    {
      search: querySearch.trim(),
      filters: allFilters,
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
      excludeInternalEnrolments: true,
      excludeWithdrawnDeleted: searchParams.get('excludeWithdrawnDeleted') === 'true',
    },
    {
      initialData: emptyPaginationResponse(),
      enabled: filtersLoaded,
    },
  );

  const { isOpen } = useDisclosure({ isOpen: Boolean(enrolmentId) });

  const getPageHeading = () => {
    const status = searchParams.get('status');
    switch (status) {
      case 'IN_PROGRESS':
        return <Trans>Active Enrolments</Trans>;
      case 'COMPLETED,WITHDRAWN':
        return <Trans>Past Enrolments</Trans>;
      case null:
      default:
        return <Trans>All Enrolments</Trans>;
    }
  };

  const onCloseHandler = useCallback(() => {
    navigate(`/enrolments?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onCloseHandler} size="xl" blockScrollOnMount={false}>
        <DrawerOverlay />
        <DrawerContent>
          <Suspense fallback={<CenteredSpinner />}>
            <EnrolmentDetails onClose={onCloseHandler} />
          </Suspense>
        </DrawerContent>
      </Drawer>
      <TableViewLayout
        pageHeader={[
          !isExpanded && (
            <IconButton
              key="expand"
              aria-label="expand secondary sidebar"
              icon={<ArrowRightFromLine />}
              variant="tertiary"
              onClick={toggleExpandSubSidebar}
              size="md"
            />
          ),
          <PageHeading key="page-heading" data-testid={testIds.Header}>
            {getPageHeading()}
          </PageHeading>,
        ]}
        pageActions={
          isTenantUsingNewReferralProcess ? <CourseReferralButton /> : <QuickReferralModal />
        }
        filters={
          <>
            <SearchInput maxW="search-input" onCommit={setQuerySearch} />
            <ProviderFilterBar />
          </>
        }
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={paginatedEnrolments.pagination.totalCount || 0}
              zero="0 enrolments"
              one="1 enrolment"
              other="# enrolments"
            />
          </Text>
        }
        table={
          <>
            <EnrolmentsTable enrolments={paginatedEnrolments.items} />

            {isFetching ? (
              <Center p={8}>
                <Spinner size="xl" />
              </Center>
            ) : paginatedEnrolments.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No enrolments found</Trans>
                </Text>
              </Center>
            ) : null}
          </>
        }
        pagination={
          paginatedEnrolments.items.length > 0 ? (
            <Pagination pagination={paginatedEnrolments.pagination} onPageChange={setPage} />
          ) : null
        }
      />
    </>
  );
}
